/**
 * Slide animation utility functions for AnimatedItem interface
 * Provides sliding entrance and exit animations with customizable directions
 */

import { interpolate, Easing } from 'remotion';

export type SlideDirection = 'top' | 'bottom' | 'left' | 'right';

/**
 * Calculate slide animation transform values
 * @param progress - Animation progress (0 to 1)
 * @param direction - Animation direction ('entrance' or 'exit')
 * @param slideDirection - Direction of slide movement
 * @param distance - Distance to slide in pixels (default: 100)
 * @returns Transform values for current animation frame
 */
export function calculateSlideTransform(
  progress: number,
  direction: 'entrance' | 'exit',
  slideDirection: SlideDirection = 'bottom',
  distance: number = 100
): { x: number; y: number; opacity: number } {
  // Validate inputs
  if (progress < 0 || progress > 1) {
    console.warn(`Invalid progress value: ${progress}. Must be between 0 and 1.`);
    progress = Math.max(0, Math.min(1, progress));
  }
  
  if (distance < 0) {
    console.warn(`Invalid distance value: ${distance}. Must be positive.`);
    distance = Math.abs(distance);
  }

  // Use Remotion's interpolate with easing for smooth animation
  const easedProgress = direction === 'entrance' 
    ? interpolate(progress, [0, 1], [0, 1], {
        easing: Easing.out(Easing.cubic)
      })
    : interpolate(progress, [0, 1], [1, 0], {
        easing: Easing.in(Easing.cubic)
      });
  
  let x = 0;
  let y = 0;
  
  // Calculate slide distance based on eased progress
  const slideDistance = distance * (1 - easedProgress);
  
  switch (slideDirection) {
    case 'top':
      y = -slideDistance;
      break;
    case 'bottom':
      y = slideDistance;
      break;
    case 'left':
      x = -slideDistance;
      break;
    case 'right':
      x = slideDistance;
      break;
  }
  
  // Fade during slide for smoother appearance using eased progress
  const opacity = easedProgress;
  
  return { x, y, opacity };
}

/**
 * Get CSS transform for slide animation with hardware acceleration
 * @param x - X translation in pixels
 * @param y - Y translation in pixels
 * @param opacity - Current opacity value
 * @returns CSS style object for slide animation
 */
export function getSlideAnimationStyle(x: number, y: number, opacity: number): React.CSSProperties {
  return {
    transform: `translate3d(${x}px, ${y}px, 0)`,
    opacity,
    willChange: 'transform, opacity',
  };
}

/**
 * Calculate slide animation for specific direction shortcuts
 */
export function calculateSlideUpAnimation(progress: number, direction: 'entrance' | 'exit'): React.CSSProperties {
  const { x, y, opacity } = calculateSlideTransform(progress, direction, 'bottom');
  return getSlideAnimationStyle(x, y, opacity);
}

export function calculateSlideDownAnimation(progress: number, direction: 'entrance' | 'exit'): React.CSSProperties {
  const { x, y, opacity } = calculateSlideTransform(progress, direction, 'top');
  return getSlideAnimationStyle(x, y, opacity);
}

export function calculateSlideLeftAnimation(progress: number, direction: 'entrance' | 'exit'): React.CSSProperties {
  const { x, y, opacity } = calculateSlideTransform(progress, direction, 'right');
  return getSlideAnimationStyle(x, y, opacity);
}

export function calculateSlideRightAnimation(progress: number, direction: 'entrance' | 'exit'): React.CSSProperties {
  const { x, y, opacity } = calculateSlideTransform(progress, direction, 'left');
  return getSlideAnimationStyle(x, y, opacity);
}